import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { familyMembersAPI } from '../../services/api';
import { Modal, ConfirmModal } from '../ui';
import type { Client, FamilyMember } from '../../types';
import { calculateAge } from '../../utils/validation';
import toast from 'react-hot-toast';
import { ClientProfileHeader } from './ClientProfileHeader';
import { ClientSummaryStats } from './ClientSummaryStats';
import { ClientPersonalInfoCard } from './ClientPersonalInfoCard';
import { ClientFamilySection } from './ClientFamilySection';
import { ClientPlanSection } from './ClientPlanSection';
import { FamilyMemberModal, type FamilyMemberFormData } from './FamilyMemberModal';
import {
  computeFinancialSummary,
  groupFamilyMembers
} from './clientProfileUtils';

export interface ClientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  deleteFamilyMutation: {
    mutate: (id: string) => void;
    isLoading: boolean;
  };
  onEditClient: () => void;
}

export const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  isOpen,
  onClose,
  client,
  deleteFamilyMutation,
  onEditClient
}) => {
  const navigate = useNavigate();
  const [isFamilyModalOpen, setIsFamilyModalOpen] = useState(false);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState<FamilyMember | null>(null);
  const [familyConfirm, setFamilyConfirm] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });
  const queryClient = useQueryClient();

  const groupedFamilyMembers = useMemo(() => groupFamilyMembers(client), [client]);
  const totalFamilyMembers = useMemo(
    () => Object.values(groupedFamilyMembers).reduce((sum, group) => sum + group.length, 0),
    [groupedFamilyMembers]
  );

  const financial = useMemo(
    () => (client ? computeFinancialSummary(client as Parameters<typeof computeFinancialSummary>[0]) : null),
    [client]
  );

  const createFamilyMutation = useMutation(familyMembersAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('clients');
      if (client?.id) queryClient.invalidateQueries(['client', client.id]);
      toast.success('Familiar agregado exitosamente');
      setIsFamilyModalOpen(false);
      setSelectedFamilyMember(null);
    },
    onError: (error: { response?: { data?: { error?: string } } }) => {
      toast.error(error.response?.data?.error || 'Error al agregar el familiar');
    }
  });

  const updateFamilyMutation = useMutation(
    ({ id, data }: { id: string; data: FamilyMemberFormData }) => familyMembersAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        if (client?.id) queryClient.invalidateQueries(['client', client.id]);
        toast.success('Familiar actualizado exitosamente');
        setIsFamilyModalOpen(false);
        setSelectedFamilyMember(null);
      },
      onError: (error: { response?: { data?: { error?: string } } }) => {
        toast.error(error.response?.data?.error || 'Error al actualizar el familiar');
      }
    }
  );

  const handleAddFamilyMember = () => {
    setSelectedFamilyMember(null);
    setIsFamilyModalOpen(true);
  };

  const handleEditFamilyMember = (familyMember: FamilyMember) => {
    setSelectedFamilyMember(familyMember);
    setIsFamilyModalOpen(true);
  };

  const handleSaveFamilyMember = (data: FamilyMemberFormData) => {
    if (selectedFamilyMember) {
      updateFamilyMutation.mutate({ id: selectedFamilyMember.id, data });
    } else {
      createFamilyMutation.mutate({ ...data, clientId: client?.id || '' });
    }
  };

  const handleDeleteFamilyMember = (id: string) => {
    setFamilyConfirm({
      isOpen: true,
      title: 'Eliminar Familiar',
      message: '¿Está seguro de que desea eliminar este familiar? Esta acción no se puede deshacer.',
      type: 'danger',
      onConfirm: () => {
        deleteFamilyMutation.mutate(id);
        setFamilyConfirm((prev) => ({ ...prev, isOpen: false }));
      }
    });
  };

  if (!client) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={<span className="text-base font-semibold text-slate-800">Vista rápida del cliente</span>}
        size="2xl"
      >
        <div className="max-h-[min(78vh,820px)] space-y-4 overflow-y-auto pr-1">
          <ClientProfileHeader
            variant="modal"
            client={client}
            onViewFullDetail={() => {
              navigate(`/clients/${client.id}`);
              onClose();
            }}
            onEdit={onEditClient}
            onRegisterPayment={() => {
              navigate('/payments');
              toast('En Pagos, seleccione este cliente para registrar un cobro.');
            }}
            onViewInvoices={() => {
              navigate('/cobros?tab=facturas');
            }}
            onAddFamily={handleAddFamilyMember}
            onViewHistory={() => {
              navigate('/payments');
              toast('Historial disponible desde Pagos y desde la ficha completa.');
            }}
          />

          {financial && (
            <ClientSummaryStats
              client={client}
              financial={financial}
              familyCount={totalFamilyMembers}
              showServicesPlaceholder={false}
            />
          )}

          <ClientPersonalInfoCard client={client} />

          <ClientFamilySection
            client={client}
            totalCount={totalFamilyMembers}
            grouped={groupedFamilyMembers}
            onAdd={handleAddFamilyMember}
            onEdit={handleEditFamilyMember}
            onDelete={handleDeleteFamilyMember}
            calculateAge={calculateAge}
            compact
          />

          <ClientPlanSection client={client} />
        </div>
      </Modal>

      <FamilyMemberModal
        isOpen={isFamilyModalOpen}
        onClose={() => setIsFamilyModalOpen(false)}
        familyMember={selectedFamilyMember}
        clientId={client.id}
        client={client}
        onSave={handleSaveFamilyMember}
        isLoading={createFamilyMutation.isLoading || updateFamilyMutation.isLoading}
      />

      <ConfirmModal
        isOpen={familyConfirm.isOpen}
        onClose={() => setFamilyConfirm((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={familyConfirm.onConfirm}
        title={familyConfirm.title}
        message={familyConfirm.message}
        type={familyConfirm.type}
        isLoading={deleteFamilyMutation.isLoading}
      />
    </>
  );
};
